import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { WALLPAPERS, CURSOR_OPTIONS } from '@/data/display-settings-data';
import type { WallpaperOption, CursorOption } from '@/data/types';

export type AppId = 'browser' | 'trash' | 'terminal' | 'settings' | 'notes' | 'display' | 'profile' | 'projects' | 'music' | 'focus' | 'contact' | 'games' | 'analytics' | 'achievements';

export type ColorMode = 'system' | 'light' | 'dark';
export type CursorStyle = 'default' | 'pointer' | 'crosshair' | 'custom';
export type ThemeMode = 'modern' | 'classic' | 'tahoe';
export type GlassIntensity = 'soft' | 'medium' | 'deep';
export type GlassVariant = 'regular' | 'clear';

// Re-export for backwards compatibility
export { WALLPAPERS, CURSOR_OPTIONS };
export type { WallpaperOption, CursorOption };

export interface DisplaySettings {
  colorMode: ColorMode;
  wallpaperId: string;
  cursorId: string;
  theme: ThemeMode;
  glassIntensity: GlassIntensity;
  glassVariant: GlassVariant;
}

type PersistedDisplaySettings = Partial<Omit<DisplaySettings, 'theme' | 'glassIntensity' | 'glassVariant'>> & {
  theme?: ThemeMode | 'modern' | 'classic';
  glassEnabled?: boolean;
  glassIntensity?: GlassIntensity | 'subtle' | 'strong';
  glassVariant?: GlassVariant | 'light' | 'dark' | 'adaptive';
};

export interface WindowState {
  id: AppId;
  title: string;
  component: ReactNode;
  isOpen: boolean;
  isMinimized: boolean;
  isMaximized?: boolean;
  zIndex: number;
  position?: { x: number; y: number };
  size?: { width: number; height: number };
  icon?: ReactNode;
}

// Serializable window state for persistence
interface PersistedWindowState {
  id: AppId;
  isOpen: boolean;
  isMinimized: boolean;
  isMaximized: boolean;
  position?: { x: number; y: number };
  size?: { width: number; height: number };
}

interface OSContextType {
  windows: WindowState[];
  openWindow: (id: AppId) => void;
  closeWindow: (id: AppId) => void;
  minimizeWindow: (id: AppId) => void;
  toggleMaximizeWindow: (id: AppId) => void;
  focusWindow: (id: AppId) => void;
  registerWindow: (window: WindowState) => void;
  updateWindowPosition: (id: AppId, position: { x: number; y: number }) => void;
  updateWindowSize: (id: AppId, size: { width: number; height: number }) => void;
  activeWindowId: AppId | null;
  // Display settings
  displaySettings: DisplaySettings;
  setColorMode: (mode: ColorMode) => void;
  setWallpaper: (wallpaperId: string) => void;
  setCursor: (cursorId: string) => void;
  setTheme: (theme: ThemeMode) => void;
  setGlassIntensity: (intensity: GlassIntensity) => void;
  setGlassVariant: (variant: GlassVariant) => void;
  // Computed values
  currentWallpaper: WallpaperOption | undefined;
  effectiveColorMode: 'light' | 'dark';
  // Sidebar state
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;
}

const OSContext = createContext<OSContextType | undefined>(undefined);

const DEFAULT_DISPLAY_SETTINGS: DisplaySettings = {
  colorMode: 'light',
  wallpaperId: 'tahoe-blue-glass',
  cursorId: 'default',
  theme: 'tahoe',
  glassIntensity: 'medium',
  glassVariant: 'regular',
};

// Get initial color mode from system preference
function getSystemColorMode(): 'light' | 'dark' {
  if (typeof window !== 'undefined') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

// Load persisted window states from localStorage
function loadPersistedWindows(): Map<AppId, PersistedWindowState> {
  try {
    const saved = localStorage.getItem('windowStates');
    if (saved) {
      const states: PersistedWindowState[] = JSON.parse(saved);
      return new Map(states.map(s => [s.id, s]));
    }
  } catch (e) {
    console.error('Failed to load window states:', e);
  }
  return new Map();
}

// Save window states to localStorage
function persistWindows(windows: WindowState[]) {
  const statesToPersist: PersistedWindowState[] = windows.map(w => ({
    id: w.id,
    isOpen: w.isOpen,
    isMinimized: w.isMinimized,
    isMaximized: w.isMaximized || false,
    position: w.position,
    size: w.size,
  }));
  localStorage.setItem('windowStates', JSON.stringify(statesToPersist));
}

function normalizeGlassIntensity(intensity: PersistedDisplaySettings['glassIntensity']): GlassIntensity {
  if (intensity === 'subtle') return 'soft';
  if (intensity === 'strong') return 'deep';
  if (intensity === 'soft' || intensity === 'medium' || intensity === 'deep') return intensity;
  return DEFAULT_DISPLAY_SETTINGS.glassIntensity;
}

function normalizeGlassVariant(variant: PersistedDisplaySettings['glassVariant']): GlassVariant {
  if (variant === 'clear') return 'clear';
  return 'regular';
}

function normalizeColorMode(colorMode: PersistedDisplaySettings['colorMode']): ColorMode {
  if (colorMode === 'system' || colorMode === 'dark' || colorMode === 'light') return colorMode;
  return DEFAULT_DISPLAY_SETTINGS.colorMode;
}

function normalizeDisplaySettings(saved?: PersistedDisplaySettings | null): DisplaySettings {
  if (!saved) return DEFAULT_DISPLAY_SETTINGS;

  return {
    ...DEFAULT_DISPLAY_SETTINGS,
    ...saved,
    colorMode: normalizeColorMode(saved.colorMode),
    theme: saved.glassEnabled ? 'tahoe' : (saved.theme === 'tahoe' ? 'tahoe' : saved.theme === 'classic' ? 'classic' : 'modern'),
    glassIntensity: normalizeGlassIntensity(saved.glassIntensity),
    glassVariant: normalizeGlassVariant(saved.glassVariant),
  };
}

export function OSProvider({ children }: { children: ReactNode }) {
  const [windows, setWindows] = useState<WindowState[]>([]);
  const [activeWindowId, setActiveWindowId] = useState<AppId | null>(null);
  const [maxZIndex, setMaxZIndex] = useState(100);
  const [persistedStates] = useState(() => loadPersistedWindows());
  
  // Sidebar collapsed state with localStorage persistence
  const [sidebarCollapsed, setSidebarCollapsedState] = useState<boolean>(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });

  const setSidebarCollapsed = (collapsed: boolean) => {
    setSidebarCollapsedState(collapsed);
    localStorage.setItem('sidebarCollapsed', JSON.stringify(collapsed));
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };
  
  // Display settings state with localStorage persistence
  const [displaySettings, setDisplaySettings] = useState<DisplaySettings>(() => {
    const saved = localStorage.getItem('displaySettings');
    if (saved) {
      return normalizeDisplaySettings(JSON.parse(saved));
    }
    return DEFAULT_DISPLAY_SETTINGS;
  });

  // Persist window states whenever they change
  useEffect(() => {
    if (windows.length > 0) {
      persistWindows(windows);
    }
  }, [windows]);

  // Save display settings to localStorage
  useEffect(() => {
    localStorage.setItem('displaySettings', JSON.stringify(displaySettings));
  }, [displaySettings]);

  // Compute effective color mode (resolve 'system' to actual value)
  const [systemPreference, setSystemPreference] = useState<'light' | 'dark'>(getSystemColorMode());
  
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemPreference(e.matches ? 'dark' : 'light');
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  const effectiveColorMode = displaySettings.colorMode === 'system' 
    ? systemPreference 
    : displaySettings.colorMode;

  // Apply color mode to document
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark', 'tahoe');
    root.classList.add(effectiveColorMode);
    if (displaySettings.theme === 'tahoe') {
      root.classList.add('tahoe');
    }
    
    // Update CSS variables for color mode
    if (displaySettings.theme === 'tahoe') {
      if (effectiveColorMode === 'dark') {
        root.style.setProperty('--os-bg', '#0f172a');
        root.style.setProperty('--os-text', '#f8fafc');
        root.style.setProperty('--os-surface', 'rgba(15, 23, 42, 0.78)');
        root.style.setProperty('--os-surface-hover', 'rgba(30, 41, 59, 0.86)');
        root.style.setProperty('--os-border', 'rgba(148, 163, 184, 0.28)');
      } else {
        root.style.setProperty('--os-bg', '#edf7ff');
        root.style.setProperty('--os-text', '#0f172a');
        root.style.setProperty('--os-surface', 'rgba(255, 255, 255, 0.82)');
        root.style.setProperty('--os-surface-hover', 'rgba(255, 255, 255, 0.92)');
        root.style.setProperty('--os-border', 'rgba(255, 255, 255, 0.58)');
      }
    } else if (effectiveColorMode === 'light') {
      root.style.setProperty('--os-bg', '#f5f5f7');
      root.style.setProperty('--os-text', '#1d1d1f');
      root.style.setProperty('--os-surface', '#ffffff');
      root.style.setProperty('--os-surface-hover', '#f0f0f0');
      root.style.setProperty('--os-border', '#d2d2d7');
    } else {
      root.style.setProperty('--os-bg', '#1d1f27');
      root.style.setProperty('--os-text', '#ffffff');
      root.style.setProperty('--os-surface', '#2c2c2e');
      root.style.setProperty('--os-surface-hover', '#3a3a3c');
      root.style.setProperty('--os-border', '#3a3a3c');
    }
  }, [effectiveColorMode, displaySettings.theme]);

  // Apply cursor style
  useEffect(() => {
    const cursorOption = CURSOR_OPTIONS.find(c => c.id === displaySettings.cursorId);
    if (cursorOption) {
      document.body.style.cursor = cursorOption.cursor;
    }
  }, [displaySettings.cursorId]);

  // Get current wallpaper
  const currentWallpaper = WALLPAPERS.find(w => w.id === displaySettings.wallpaperId);

  // Display settings setters
  const setColorMode = (mode: ColorMode) => {
    setDisplaySettings(prev => ({ ...prev, colorMode: mode }));
  };

  const setWallpaper = (wallpaperId: string) => {
    setDisplaySettings(prev => ({ ...prev, wallpaperId }));
  };

  const setCursor = (cursorId: string) => {
    setDisplaySettings(prev => ({ ...prev, cursorId }));
  };

  const setTheme = (theme: ThemeMode) => {
    setDisplaySettings(prev => ({ ...prev, theme }));
  };

  const setGlassIntensity = (glassIntensity: GlassIntensity) => {
    setDisplaySettings(prev => ({ ...prev, glassIntensity }));
  };

  const setGlassVariant = (glassVariant: GlassVariant) => {
    setDisplaySettings(prev => ({ ...prev, glassVariant }));
  };

  const registerWindow = (window: WindowState) => {
    setWindows((prev) => {
      if (prev.find((w) => w.id === window.id)) return prev;
      
      // Apply persisted state if available
      const persisted = persistedStates.get(window.id);
      if (persisted) {
        return [...prev, {
          ...window,
          isOpen: persisted.isOpen,
          isMinimized: persisted.isMinimized,
          isMaximized: persisted.isMaximized,
          position: persisted.position || window.position,
          size: persisted.size || window.size,
        }];
      }
      
      return [...prev, window];
    });
  };

  // Update window position (for drag)
  const updateWindowPosition = (id: AppId, position: { x: number; y: number }) => {
    setWindows((prev) =>
      prev.map((w) => (w.id === id ? { ...w, position } : w))
    );
  };

  // Update window size (for resize)
  const updateWindowSize = (id: AppId, size: { width: number; height: number }) => {
    setWindows((prev) =>
      prev.map((w) => (w.id === id ? { ...w, size } : w))
    );
  };

  const focusWindow = (id: AppId) => {
    setActiveWindowId(id);
    setMaxZIndex((prev) => prev + 1);
    setWindows((prev) =>
      prev.map((w) =>
        w.id === id ? { ...w, zIndex: maxZIndex + 1, isMinimized: false } : w
      )
    );
  };

  const openWindow = (id: AppId) => {
    setWindows((prev) =>
      prev.map((w) => {
        if (w.id === id) {
          return { ...w, isOpen: true, isMinimized: false };
        }
        return w;
      })
    );
    focusWindow(id);
  };

  const closeWindow = (id: AppId) => {
    setWindows((prev) =>
      prev.map((w) => (w.id === id ? { ...w, isOpen: false } : w))
    );
    if (activeWindowId === id) {
      setActiveWindowId(null);
    }
  };

  const minimizeWindow = (id: AppId) => {
    setWindows((prev) =>
      prev.map((w) => (w.id === id ? { ...w, isMinimized: true } : w))
    );
    if (activeWindowId === id) {
      setActiveWindowId(null);
    }
  };

  const toggleMaximizeWindow = (id: AppId) => {
     setWindows((prev) => 
       prev.map((w) => (w.id === id ? { ...w, isMaximized: !w.isMaximized } : w))
     );
     focusWindow(id);
  };

  return (
    <OSContext.Provider
      value={{
        windows,
        openWindow,
        closeWindow,
        minimizeWindow,
        toggleMaximizeWindow,
        focusWindow,
        registerWindow,
        updateWindowPosition,
        updateWindowSize,
        activeWindowId,
        // Display settings
        displaySettings,
        setColorMode,
        setWallpaper,
        setCursor,
        setTheme,
        setGlassIntensity,
        setGlassVariant,
        currentWallpaper,
        effectiveColorMode,
        // Sidebar state
        sidebarCollapsed,
        setSidebarCollapsed,
        toggleSidebar,
      }}
    >
      {children}
    </OSContext.Provider>
  );
}

export function useOS() {
  const context = useContext(OSContext);
  if (context === undefined) {
    throw new Error('useOS must be used within an OSProvider');
  }
  return context;
}
